import { z } from "zod";
// https://www.pubnub.com/docs/chat/sdks/messages/message-history
const pubnubTimestampFactor = BigInt(10000);
export function pubNubTimeTokenToMs(timetoken) {
    return Number(timetoken / pubnubTimestampFactor);
}
export const getPubNubTimestamp = (timeMs) => {
    return BigInt(timeMs) * pubnubTimestampFactor;
};
export function convertToPubNubTimetoken(time) {
    switch (typeof time) {
        case "bigint":
            return time;
        case "number":
            return getPubNubTimestamp(time);
        default:
            return undefined;
    }
}
export const STAGE_TEN_SOURCE_NAME = "stageten";
export const STAGE_TEN_CHAT_MESSAGE_TYPE = "chat";
export const STAGE_TEN_NOTIFICATION_MESSAGE_TYPE = "notification";
const MessageTypes = ["chat", "notification"];
export function isMessageType(value) {
    // Validation function, so type assertion is used to assert that the given value is a valid message type.
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return MessageTypes.includes(value);
}
export const ReactionKeys = [
    "heart",
    "satisfied",
    "open_mouth",
    "cry",
    "angry",
    "thumbsup",
];
export const defaultMessageReactionCounts = Object.freeze(getDefaultMessageReactionCounts());
export function getDefaultMessageReactionCounts() {
    return ReactionKeys.reduce((defaults, reactionKey) => {
        defaults[reactionKey] = {
            reactionKey,
            count: 0,
        };
        return defaults;
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    }, {});
}
export const ReactionMap = {
    heart: "❤️",
    satisfied: "😆",
    // eslint-disable-next-line @typescript-eslint/naming-convention
    open_mouth: "😮",
    cry: "😢",
    angry: "😠",
    thumbsup: "👍",
};
export const ReactionEventParser = z.record(z.enum(ReactionKeys), z.array(z.object({
    actionTimetoken: z.coerce.bigint(),
    uuid: z.string(),
})));
export function transformReactionEvent(event, userId) {
    return ReactionKeys.reduce((counts, key) => {
        var _a, _b;
        const count = (_a = event[key]) === null || _a === void 0 ? void 0 : _a.length;
        if (!count) {
            return counts;
        }
        const sentAction = (_b = event[key]) === null || _b === void 0 ? void 0 : _b.find(({ uuid }) => uuid === userId);
        counts[key] = {
            reactionKey: key,
            count,
            sentActionTimetoken: sentAction === null || sentAction === void 0 ? void 0 : sentAction.actionTimetoken,
        };
        return counts;
    }, getDefaultMessageReactionCounts());
}
const ChatMessageEventParser = z.object({
    // Optional because clients that don't have optimistic updates
    // will not send this field. We may eventually be able to make
    // this a required field.
    messageKey: z.string().optional(),
    userId: z.string(),
    isChannelOwner: z.boolean(),
    displayName: z.string(),
    text: z.string(),
    sourceName: z.string(),
    sourceIconUrl: z.string().optional(),
});
const NotificationMessageParser = z.object({
    userId: z.string(),
    title: z.string().optional(),
    text: z.string(),
    sourceName: z.string(),
    sourceIconUrl: z.string().optional(),
});
const ChatPubNubMessageEventParser = z.object({
    timetoken: z.coerce.bigint(),
    customMessageType: z.literal("chat").default("chat"),
    message: ChatMessageEventParser,
    actions: z
        .object({
        reaction: ReactionEventParser.optional(),
    })
        .optional(),
});
const NotificationPubNubMessageEventParser = z.object({
    timetoken: z.coerce.bigint(),
    customMessageType: z.literal("notification"),
    message: NotificationMessageParser,
});
export const PubNubMessageEventParser = z.union([
    ChatPubNubMessageEventParser,
    NotificationPubNubMessageEventParser,
]);
export function createTextMessage(event, userId) {
    var _a, _b;
    const parsedEvent = PubNubMessageEventParser.safeParse(event);
    if (!parsedEvent.success ||
        !isMessageType(parsedEvent.data.customMessageType)) {
        return null;
    }
    const messageType = parsedEvent.data.customMessageType;
    switch (messageType) {
        case "chat": {
            const reaction = (_a = parsedEvent.data.actions) === null || _a === void 0 ? void 0 : _a.reaction;
            return {
                type: messageType,
                data: Object.assign(Object.assign({}, parsedEvent.data.message), { messageKey: (_b = parsedEvent.data.message.messageKey) !== null && _b !== void 0 ? _b : parsedEvent.data.timetoken.toString(), timetoken: parsedEvent.data.timetoken, timestampMs: pubNubTimeTokenToMs(parsedEvent.data.timetoken), isMe: parsedEvent.data.message.userId === userId, reactions: reaction
                        ? transformReactionEvent(reaction, userId)
                        : getDefaultMessageReactionCounts() }),
            };
        }
        case "notification": {
            return {
                type: messageType,
                data: Object.assign(Object.assign({}, parsedEvent.data.message), { timetoken: parsedEvent.data.timetoken, timestampMs: pubNubTimeTokenToMs(parsedEvent.data.timetoken) }),
            };
        }
        default: {
            return null;
        }
    }
}
export function parseChatMessageFetchResponse(rawMessages, userId) {
    const parsedMessages = [];
    for (let i = 0; i < rawMessages.length; i++) {
        const message = createTextMessage(rawMessages[i], userId);
        if (message) {
            parsedMessages.push(message);
        }
    }
    return parsedMessages;
}
export function isValidReactionKey(key) {
    return Object.hasOwn(ReactionMap, key);
}
